import React, { useEffect, useRef, useState } from "react";

/* The one canonical AI launcher: a round button, bottom-right by default, that
 * you can DRAG anywhere (it gets out of the way) — a quick tap still opens the
 * assistant. The chosen spot is remembered across screens and sessions.
 * Used for the global screen assistant, the per-device assistant, and the
 * customer portal so the AI entry point looks + behaves the same everywhere. */

const STORAGE_KEY = "ai-launcher-pos";
const SIZE = 56; // w-14 h-14
const DRAG_THRESHOLD = 5; // px of movement before it's a drag, not a tap

function loadPos(): { x: number; y: number } | null {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

export default function AILauncherButton({
  onClick,
  title,
  active = false,
  icon = "✨",
  colorClass = "bg-indigo-600 hover:bg-indigo-700",
  activeColorClass = "bg-indigo-700 ring-2 ring-indigo-300",
}: {
  onClick: () => void;
  title: string;
  active?: boolean;
  icon?: React.ReactNode;
  colorClass?: string;
  activeColorClass?: string;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(loadPos);
  const btnRef = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(
    null,
  );
  const posRef = useRef(pos);
  posRef.current = pos;

  const clamp = (x: number, y: number) => ({
    x: Math.min(Math.max(4, x), window.innerWidth - SIZE - 4),
    y: Math.min(Math.max(4, y), window.innerHeight - SIZE - 4),
  });

  const onPointerDown = (e: React.PointerEvent) => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, moved: false };
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    setPos(clamp(d.ox + dx, d.oy + dy));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    try {
      btnRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!d) return;
    if (!d.moved) {
      onClick(); // a tap opens the assistant
    } else if (posRef.current) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(posRef.current));
      } catch {
        /* ignore */
      }
    }
  };

  // Keep it on-screen if the window is resized.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const style: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : {};

  return (
    <button
      ref={btnRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      title={title}
      aria-label={title}
      style={style}
      className={`fixed bottom-5 right-5 z-40 rounded-full shadow-lg w-14 h-14 flex items-center justify-center text-xl text-white transition touch-none cursor-grab active:cursor-grabbing ${
        active ? activeColorClass : colorClass
      }`}
    >
      {icon}
    </button>
  );
}
